import BackHandOutlinedIcon from "@mui/icons-material/BackHandOutlined";
import { Box, IconButton, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import saveAs from "file-saver";
import useAPI from "../../../../../../api/hooks/useAPI";
import useColors from "../../../../../../api/hooks/useColors";
import { controlKeys } from "../../../../../../api/queryKeys";
import CustomTooltip from "../../../../../../components/CustomTooltip";
import LoadingIcon from "../../../../../../components/LoadingIcon";
import { Toast } from "../../../../../../components/Toast";
import { useFileNaming } from "../../../../../../hooks/useFileNaming";
import SvgToIcon from "../../../../../../icons/SvgToIcon";
import { icons } from "../../../../../stack/environment/icons/iconExport";

const PlaybookButton = ({ control, sx, outline = false }) => {
  const colors = useColors();
  const { generatePdfFileName } = useFileNaming();

  const {
    trackEvent,
    downloadPlaybook,
    isPlaybookAvailable: isPlaybookAvailableAPI,
  } = useAPI();

  const { data: isPlaybookAvailable, isLoading: loading } = useQuery({
    queryKey: controlKeys.isPlaybookAvailable(control?.control_id),
    queryFn: () => isPlaybookAvailableAPI(control?.control_id),
    select: (response) => response.data,
    enabled: control !== null,
  });

  const handleRequestPlaybook = () => {
    // NOTE: this log message is monitored by a Coralogix alert - DO NOT CHANGE IT
    // see alerts on https://breezesecurity.app.eu2.coralogix.com/#/alerts
    const message =
      "User requested playbook for control: " + control?.control_id;
    trackEvent(message).then();
    Toast.success("Request sent");
  };

  const handleDownloadPlaybook = () => {
    const message =
      "User clicked to download the playbook for control: " +
      control?.control_id;
    trackEvent(message);

    if (isPlaybookAvailable) {
      Toast.info("Downloading remediation playbook..");
      downloadPlaybook(control?.control_id)
        .then((response) => {
          const blob = new Blob([response.data], { type: "application/pdf" });
          const name = generatePdfFileName(
            `remediation_playbook_${control?.control_id}`
          );
          saveAs(blob, name);
        })
        .catch((error) => {
          Toast.error("Error downloading PDF file, please try again later");
          trackEvent("Failed to download remediation playbook", {
            error: error.message,
          });
        });
    } else {
      Toast.info("A remediation playbook for this gap will be available soon");
    }
  };

  const handleMouseEnter = () => {
    const message =
      "User hovered the " +
      (!isPlaybookAvailable ? "Request" : "Download") +
      " Playbook button for control: " +
      control?.control_id;
    trackEvent(message);
  };

  return (
    <CustomTooltip
      title={
        !loading && !isPlaybookAvailable ? (
          <>
            The playbook for this gap isn't available yet.
            <br />
            Click to request it, and we'll notify you when it's available.
          </>
        ) : outline ? (
          ""
        ) : (
          "Download Playbook"
        )
      }
      placement="top"
    >
      <Box onMouseEnter={handleMouseEnter}>
        {!outline ? (
          <IconButton
            sx={{
              background: colors.common_white,
              color: colors.action_active,
              width: "32px",
              height: "32px",
              border: "1px solid " + colors.grey_300,
              borderRadius: "8px",
              textTransform: "none",
              whiteSpace: "nowrap",
              display: !isPlaybookAvailable ? "none" : "flex",

              padding: 0,
              "&:hover": {
                background: colors.grey_8percent,
                borderColor: colors.gray_main,
              },
              //...props.sx,
            }}
            onClick={
              !isPlaybookAvailable
                ? handleRequestPlaybook
                : handleDownloadPlaybook
            }
          >
            {!loading ? (
              !isPlaybookAvailable ? (
                <BackHandOutlinedIcon
                  sx={{
                    width: "18px",
                  }}
                />
              ) : (
                <SvgToIcon IconLogo={icons.ActionDownloadIcon} width="20px" />
              )
            ) : (
              <LoadingIcon size={18} />
            )}
          </IconButton>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 1,
              color: colors.primary_main,
              cursor: "pointer",
            }}
            onClick={
              !isPlaybookAvailable
                ? handleRequestPlaybook
                : handleDownloadPlaybook
            }
          >
            {!loading &&
              (!isPlaybookAvailable ? (
                <BackHandOutlinedIcon sx={{ width: "20px" }} />
              ) : (
                <SvgToIcon IconLogo={icons.ActionDownloadIcon} width="20px" />
              ))}
            <Box>
              {loading ? (
                <LoadingIcon size={8} />
              ) : (
                <Typography variant="overline" fontWeight={500}>
                  {!isPlaybookAvailable
                    ? "Request Playbook"
                    : "Download Playbook"}
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </CustomTooltip>
  );
};

export default PlaybookButton;
